import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import ModuleSearchControls, {
  ModuleFilterMenu,
  ModulePerPageMenu,
  ModuleRadioOption,
  ModuleSortMenu,
} from "../../components/ModuleSearchControls";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";
import PageAlert from "../../components/PageAlert/PageAlert";
import DataTable, { DataTableActions, DataTableFooter } from "../../components/DataTable";
import ArticlesNav from "./components/ArticlesNav";
import { resolveApiError } from "../../utils/apiError";
import {
  deleteArticle,
  getArticleCategories,
  getArticleCategoryName,
  getArticleSubcategoryName,
  formatArticleDate,
  getArticles,
  normalizePaginated,
  splitArticleCategories,
} from "./articleApi";
import "./articles.scss";

const SORT_OPTIONS = [
  { value: "created_at", label: "Created date" },
  { value: "updated_at", label: "Updated date" },
  { value: "title", label: "Title" },
  { value: "status", label: "Status" },
  { value: "visibility", label: "Visibility" },
];

const ArticleList = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [debouncedKeyword, setDebouncedKeyword] = useState("");
  const [status, setStatus] = useState("all");
  const [visibility, setVisibility] = useState("all");
  const [categoryId, setCategoryId] = useState("");
  const [subcategoryId, setSubcategoryId] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [perPageOpen, setPerPageOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedKeyword(keyword.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [keyword]);

  useEffect(() => {
    let active = true;
    getArticleCategories({ page: 1, per_page: 200 })
      .then((categoryResponse) => {
        if (!active) return;
        setCategories(normalizePaginated(categoryResponse).items);
      })
      .catch((err) => {
        if (active) resolveApiError(err, setError, navigate);
      });
    return () => {
      active = false;
    };
  }, [navigate]);

  const { parents: parentCategories, childrenByParent } = useMemo(
    () => splitArticleCategories(categories),
    [categories]
  );
  const subcategoryOptions = useMemo(() => {
    if (categoryId) return childrenByParent.get(Number(categoryId)) || [];
    return parentCategories.flatMap((parent) => childrenByParent.get(Number(parent.id)) || []);
  }, [categoryId, childrenByParent, parentCategories]);

  const params = useMemo(
    () => ({
      page,
      per_page: perPage,
      sort_by: sortBy,
      sort_order: sortOrder,
      keyword: debouncedKeyword || undefined,
      status: status === "all" ? undefined : status,
      visibility: visibility === "all" ? undefined : visibility,
      category_id: subcategoryId || categoryId || undefined,
    }),
    [page, perPage, sortBy, sortOrder, debouncedKeyword, status, visibility, categoryId, subcategoryId]
  );

  const loadArticles = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getArticles(params);
      const result = normalizePaginated(response);
      setArticles(result.items);
      setMeta(result.meta);
    } catch (err) {
      resolveApiError(err, setError, navigate);
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    loadArticles();
  }, [loadArticles]);

  const resetFilters = () => {
    setKeyword("");
    setDebouncedKeyword("");
    setStatus("all");
    setVisibility("all");
    setCategoryId("");
    setSubcategoryId("");
    setSortBy("created_at");
    setSortOrder("desc");
    setPerPage(20);
    setPage(1);
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await deleteArticle(pendingDelete.id);
      setPendingDelete(null);
      setNotice("Article deleted.");
      await loadArticles();
    } catch (err) {
      resolveApiError(err, setError, navigate);
    } finally {
      setDeleting(false);
    }
  };

  const activeFilterCount = [
    status !== "all",
    visibility !== "all",
    categoryId,
    subcategoryId,
  ].filter(Boolean).length;

  return (
    <div className="articles-page">
      {notice && (
        <PageAlert variant="success" onDismiss={() => setNotice("")}>{notice}</PageAlert>
      )}
      {error && <PageAlert variant="error" onDismiss={() => setError("")}>{error}</PageAlert>}

      <div className="articles-page__heading">
        <div>
          <h2>Articles</h2>
          <p>Create and organise news, guides and website content with block editing.</p>
        </div>
        <button
          className="ui-button ui-button--primary"
          type="button"
          onClick={() => navigate("/admin/articles/create")}
        >
          <Icon icon="ph:plus" height={14} /> New article
        </button>
      </div>

      <ArticlesNav />

      <section className="articles-page__card">
        <ModuleSearchControls
          value={keyword}
          onChange={setKeyword}
          placeholder="Search articles"
        >
          <ModuleFilterMenu
            open={filterOpen}
            onToggle={() => {
              setFilterOpen((open) => !open);
              setSortOpen(false);
              setPerPageOpen(false);
            }}
            onClose={() => setFilterOpen(false)}
            count={activeFilterCount}
            ariaLabel="Article filters"
          >
            <fieldset>
              <legend>Status</legend>
              {[
                ["all", "All statuses"],
                ["draft", "Draft"],
                ["published", "Published"],
                ["archived", "Archived"],
              ].map(([value, label]) => (
                <ModuleRadioOption
                  key={value}
                  name="article-status"
                  checked={status === value}
                  onChange={() => {
                    setStatus(value);
                    setPage(1);
                  }}
                  plain
                >
                  {label}
                </ModuleRadioOption>
              ))}
            </fieldset>
            <fieldset>
              <legend>Visibility</legend>
              {[
                ["all", "All visibility"],
                ["public", "Public"],
                ["private", "Private"],
              ].map(([value, label]) => (
                <ModuleRadioOption
                  key={value}
                  name="article-visibility"
                  checked={visibility === value}
                  onChange={() => {
                    setVisibility(value);
                    setPage(1);
                  }}
                  plain
                >
                  {label}
                </ModuleRadioOption>
              ))}
            </fieldset>
            <fieldset>
              <legend>Category</legend>
              <ModuleRadioOption
                name="article-category"
                checked={!categoryId}
                onChange={() => {
                  setCategoryId("");
                  setSubcategoryId("");
                  setPage(1);
                }}
              >
                All categories
              </ModuleRadioOption>
              {parentCategories.map((category) => (
                <ModuleRadioOption
                  key={category.id}
                  name="article-category"
                  checked={String(categoryId) === String(category.id)}
                  onChange={() => {
                    setCategoryId(String(category.id));
                    setSubcategoryId("");
                    setPage(1);
                  }}
                >
                  {category.name}
                </ModuleRadioOption>
              ))}
            </fieldset>
            <fieldset>
              <legend>Subcategory</legend>
              <ModuleRadioOption
                name="article-subcategory"
                checked={!subcategoryId}
                onChange={() => {
                  setSubcategoryId("");
                  setPage(1);
                }}
              >
                All subcategories
              </ModuleRadioOption>
              {subcategoryOptions.map((subcategory) => (
                <ModuleRadioOption
                  key={subcategory.id}
                  name="article-subcategory"
                  checked={String(subcategoryId) === String(subcategory.id)}
                  onChange={() => {
                    setSubcategoryId(String(subcategory.id));
                    setPage(1);
                  }}
                >
                  {subcategory.name}
                </ModuleRadioOption>
              ))}
            </fieldset>
          </ModuleFilterMenu>
          <ModuleSortMenu
            open={sortOpen}
            onToggle={() => {
              setSortOpen((open) => !open);
              setFilterOpen(false);
              setPerPageOpen(false);
            }}
            onClose={() => setSortOpen(false)}
            sortBy={sortBy}
            sortOrder={sortOrder}
            options={SORT_OPTIONS}
            onChangeSortBy={(value) => {
              setSortBy(value);
              setPage(1);
            }}
            onChangeSortOrder={(value) => {
              setSortOrder(value);
              setPage(1);
            }}
          />
          <ModulePerPageMenu
            open={perPageOpen}
            onToggle={() => {
              setPerPageOpen((open) => !open);
              setFilterOpen(false);
              setSortOpen(false);
            }}
            onClose={() => setPerPageOpen(false)}
            value={perPage}
            onChange={(value) => {
              setPerPage(value);
              setPage(1);
            }}
          />
          <button className="ui-button" type="button" onClick={resetFilters}>
            <Icon icon="ph:arrow-clockwise" height={14} /> Reset
          </button>
        </ModuleSearchControls>

        <DataTable
          minWidth={900}
          loading={loading}
          loadingMessage="Loading articles..."
          emptyMessage="No articles found."
          rows={articles}
          columns={[
            {
              key: "title",
              header: "Title",
              render: (article) => (
                <>
                  <strong>{article.title}</strong>
                  <small>/{article.slug}</small>
                </>
              ),
            },
            {
              key: "taxonomy",
              header: "Category / Subcategory",
              render: (article) => (
                <>
                  <strong>{getArticleCategoryName(article) || "—"}</strong>
                  <small>{getArticleSubcategoryName(article) || "No subcategory"}</small>
                </>
              ),
            },
            {
              key: "status",
              header: "Status",
              render: (article) => (
                <span className={`articles-page__status articles-page__status--${article.status}`}>
                  {article.status}
                </span>
              ),
            },
            { key: "visibility", header: "Visibility", render: (article) => article.visibility || "—" },
            { key: "created_at", header: "Created", render: (article) => formatArticleDate(article.created_at) || "—" },
            { key: "updated_at", header: "Updated", render: (article) => formatArticleDate(article.updated_at) || "—" },
            {
              key: "actions",
              header: "Actions",
              render: (article) => (
                <DataTableActions>
                  <button type="button" onClick={() => navigate(`/admin/articles/${article.id}/edit`)}>
                    Edit
                  </button>
                  <button type="button" onClick={() => navigate(`/admin/articles/${article.id}/preview`)}>
                    Preview
                  </button>
                  <button type="button" className="danger" onClick={() => setPendingDelete(article)}>
                    Delete
                  </button>
                </DataTableActions>
              ),
            },
          ]}
        />
        <DataTableFooter meta={meta} loading={loading} onPageChange={setPage} noun="articles" />
      </section>
      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete article"
        message={pendingDelete ? `Delete “${pendingDelete.title}”? This cannot be undone.` : ""}
        confirming={deleting}
        onCancel={() => !deleting && setPendingDelete(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default ArticleList;
